import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LOCALES, findLocale } from "../i18n/locales";

type Props = {
  variant?: "compact" | "full";
};

export function LanguageMenu({ variant = "compact" }: Props) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = findLocale(i18n.language);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function pick(code: string) {
    void i18n.changeLanguage(code);
    localStorage.setItem("locale", code);
    setOpen(false);
  }

  const buttonClass =
    variant === "compact"
      ? "bg-white/90 backdrop-blur rounded-xl shadow w-10 h-10 flex items-center justify-center text-sm font-bold text-primary shrink-0"
      : "bg-white/90 backdrop-blur rounded-2xl shadow-lg px-5 py-3 text-base font-bold text-primary flex items-center gap-2";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="switch language"
        aria-haspopup="menu"
        aria-expanded={open}
        className={buttonClass}
      >
        {variant === "compact" ? (
          <span>{current.shortLabel}</span>
        ) : (
          <>
            <span aria-hidden>{current.flag}</span>
            <span>{current.shortLabel}</span>
            <span aria-hidden className="text-xs text-muted">▾</span>
          </>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-40"
        >
          {LOCALES.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                role="menuitem"
                type="button"
                onClick={() => pick(l.code)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-sakura/20 ${
                  active ? "bg-sakura/10 font-bold text-primary" : "text-body"
                }`}
              >
                <span className="text-base" aria-hidden>{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {active && <span aria-hidden className="text-primary">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
